import type { CapabilityClient } from './index';
import type { OutputOf } from './generated/contracts';

export type SellerChecklist = OutputOf<'sellers.onboarding.checklist'>;

/**
 * **The onboarding checklist, after asking Stripe for a verdict the webhook may have missed.**
 *
 * The seller's KYC verdict normally arrives on Stripe's `account.updated` webhook. When it
 * does not arrive, the seller is stuck at "Set up payouts" while Stripe has them fully
 * onboarded. Stripe never re-sends an old event to a new endpoint, and a delivery that runs
 * out of retries is gone (found on `john-dev`, 2026-10-02). So while the Stripe step is next,
 * this asks `sellers.kyc.refresh` first and reads the checklist again if that activated them.
 *
 * Both clients load the checklist through this, so neither can forget the step. The refresh
 * is best effort: if Stripe is unreachable the checklist shows what the server already
 * knows, and the next load asks again.
 */
export async function sellerChecklist(client: CapabilityClient): Promise<SellerChecklist> {
  const checklist = await client.query('sellers.onboarding.checklist', {});
  const kyc = checklist.steps.find((step) => step.step === 'stripe_kyc');
  if (!kyc || kyc.done || !kyc.next) return checklist;
  try {
    const refreshed = await client.command('sellers.kyc.refresh', {}, {});
    // Not only when this call activated them: the webhook may have done it while the
    // refresh was in flight (`applied: false`, `state: 'active'`). Any state other than
    // the one this checklist was read in means the checklist is stale.
    if (refreshed.applied || refreshed.state !== checklist.state) {
      return client.query('sellers.onboarding.checklist', {});
    }
  } catch {
    // Stripe or the network: the checklist as it stands is still the truth we have.
  }
  return checklist;
}
